import { useCallback, useEffect, useMemo, useState } from 'react';
import { getArtists } from '../api';
import { getCachedBoard, setCachedBoard } from '../boardCache';
import {
  collectDateTimes,
  buildNguageByArtist,
  mergeArtistHeadings,
  computeWeeklyGainers,
} from '../artistsTable/boardModel';

// The whole ranked roster, fetched once, up front. The leaderboard ranks by %
// change over the selected timeline, which can only be done correctly over ALL
// artists - the backend paginates by reach, so fetching progressively used to
// re-rank a different subset on every scroll and let a big mover from a later
// batch jump to the top mid-scroll.
export const BOARD_FETCH_LIMIT = 1000;

// The /api/artists board, shared by the leaderboard and the homepage. getArtists
// caches per limit and de-duplicates in-flight requests, so going from one page
// to the other doesn't refetch; on native the last board also paints instantly
// from disk (boardCache) while the fresh one loads.
//
// Returns the raw pieces (data = daily_update, artistsCollection,
// nguageHistory) plus the derived lookups both pages need: allDateTimes,
// nguageByArtist, artistNames and weeklyGainers (see artistsTable/boardModel).
export default function useBoardData() {
  const [data, setData] = useState({});
  const [artistsCollection, setArtistsCollection] = useState({});
  // Per-artist NGuage trend (artist id -> [{date, value}]). Motion, % change
  // and the sparklines all derive from this.
  const [nguageHistory, setNguageHistory] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    const apply = (json) => {
      // Older payload shapes: a bare daily_update, or a bare collection.
      if (json.daily_update && json.artists_collection) {
        setData(json.daily_update);
        setArtistsCollection(json.artists_collection);
      } else if (json.artists_collection) {
        setData(json);
        setArtistsCollection(json.artists_collection);
      } else if (json.daily_update) {
        setData(json.daily_update);
        setArtistsCollection({});
      } else {
        setData(json);
        setArtistsCollection({});
      }
      setNguageHistory(json.nguage_history || {});
    };

    const fetchData = async () => {
      const cached = await getCachedBoard();
      if (cancelled) return;
      if (cached) {
        if (cached.daily_update) setData(cached.daily_update);
        if (cached.artists_collection) setArtistsCollection(cached.artists_collection);
        if (cached.nguage_history) setNguageHistory(cached.nguage_history);
        setLoading(false);
      }
      try {
        if (!cached) setLoading(true);
        setError(null);
        const json = await getArtists(BOARD_FETCH_LIMIT);
        if (cancelled) return;
        apply(json);
        setCachedBoard(json);
      } catch (err) {
        console.error('📊 Error fetching artists data:', err);
        if (!cancelled) setError(err?.message || 'Failed to load artists data. Please try again later.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [reloadToken]);

  // Memoized on the raw pieces - these used to re-run (a flatMap + Set +
  // regex over every artist's date keys, ~1000 artists) on every render,
  // including every keystroke in the leaderboard's search box.
  const allDateTimes = useMemo(() => collectDateTimes(data), [data]);
  const nguageByArtist = useMemo(() => buildNguageByArtist(nguageHistory), [nguageHistory]);
  const artistNames = useMemo(() => mergeArtistHeadings(data, artistsCollection), [data, artistsCollection]);
  const weeklyGainers = useMemo(
    () => computeWeeklyGainers({ artistNames, allDateTimes, artistsCollection, nguageByArtist }),
    [artistNames, allDateTimes, artistsCollection, nguageByArtist]
  );

  return {
    data,
    artistsCollection,
    nguageHistory,
    loading,
    error,
    reload,
    allDateTimes,
    nguageByArtist,
    artistNames,
    weeklyGainers,
  };
}
